/**
 * Ortak Müşteri Arama Yardımcısı
 * Tüm modüller (Ödeme Sözü, Senet, Ödeme, Satış, Teslimat, İcra, Global Arama)
 * bu fonksiyonu kullanarak müşteri araması yapar.
 *
 * Türkçe karakter duyarlı (case-insensitive) arama yapar.
 * "İbrahim", "ibrahim", "İBRAHİM", "ibra" hepsi aynı müşteriyi bulur.
 */

/**
 * Telefon numarasını normalize eder: rakam olmayanları siler.
 */
export function normalizePhoneForSearch(phone: string): string {
  if (!phone) return '';
  return phone.replace(/[^0-9]/g, '');
}

/**
 * Türkçe duyarlı küçük harfe çevirme.
 * "İbrahim" → "ibrahim", "İBRAHİM" → "ibrahim"
 */
export function toTurkishLower(str: string): string {
  if (!str) return '';
  return str.toLocaleLowerCase('tr-TR');
}

export interface CustomerSearchFields {
  id: string;
  full_name: string;
  phone: string;
  phone_normalized?: string;
  customer_no: string;
  identity_number?: string;
  second_phone?: string;
  [key: string]: any;
}

/**
 * Müşteri listesinde arama yapar.
 *
 * Aranan alanlar:
 * - Ad soyad (Türkçe karakter duyarsız)
 * - Telefon (normalize edilmiş)
 * - Müşteri no
 * - TC kimlik no
 *
 * @param customers - Müşteri listesi
 * @param query - Arama terimi
 * @param minChars - Minimum karakter sayısı (default: 2)
 */
export function searchCustomersLocal<T extends CustomerSearchFields>(
  customers: T[],
  query: string,
  minChars: number = 2,
): T[] {
  if (!query || query.trim().length < minChars) return [];

  const q = toTurkishLower(query.trim());
  const qPhone = normalizePhoneForSearch(query);

  return customers.filter((c) => {
    // Ad soyad (Türkçe karakter duyarsız)
    if (toTurkishLower(c.full_name || '').includes(q)) return true;

    // Telefon (sorgu en az bir rakam içermiyorsa telefon eşleşmesi atlanır,
    // aksi halde boş qPhone her telefonla eşleşir ve arama etkisiz kalır)
    if (qPhone) {
      if (normalizePhoneForSearch(c.phone || '').includes(qPhone)) return true;
      if (c.second_phone && normalizePhoneForSearch(c.second_phone).includes(qPhone)) return true;
      if (c.phone_normalized && c.phone_normalized.includes(qPhone)) return true;
    }

    // Müşteri no
    if (toTurkishLower(c.customer_no || '').includes(q)) return true;

    // TC kimlik no
    if (c.identity_number && c.identity_number.includes(q)) return true;

    return false;
  });
}

/**
 * Not (promissory_note) listesinde arama yapar.
 * Not alanlarına ek olarak bağlı müşteri bilgilerinde de arar.
 */
export interface NoteSearchFields {
  id: string;
  note_no: string;
  customer_name: string;
  customer_phone?: string;
  customer_id: string;
  note_book_no?: string;
  note_page_no?: string;
  note_sequence_no?: string;
  sale_no?: string;
  legacy_note_no?: string;
  [key: string]: any;
}

export function searchNotesLocal<T extends NoteSearchFields>(
  notes: T[],
  query: string,
  customerNoMap?: Record<string, string>,
): T[] {
  if (!query || !query.trim()) return notes;

  const q = toTurkishLower(query.trim());
  const qPhone = normalizePhoneForSearch(query);

  return notes.filter((n) => {
    // Not alanları
    if (toTurkishLower(n.note_no || '').includes(q)) return true;
    if (toTurkishLower(n.legacy_note_no || '').includes(q)) return true;
    if (toTurkishLower(n.note_book_no || '').includes(q)) return true;
    if (toTurkishLower(n.note_page_no || '').includes(q)) return true;
    if (toTurkishLower(n.note_sequence_no || '').includes(q)) return true;
    if (toTurkishLower(n.sale_no || '').includes(q)) return true;

    // Müşteri bilgileri
    if (toTurkishLower(n.customer_name || '').includes(q)) return true;
    if (qPhone && n.customer_phone && normalizePhoneForSearch(n.customer_phone).includes(qPhone)) return true;

    // Müşteri no (map'ten)
    if (customerNoMap && n.customer_id) {
      const cno = customerNoMap[n.customer_id];
      if (cno && toTurkishLower(cno).includes(q)) return true;
    }

    return false;
  });
}

/**
 * Herhangi bir metin alanında Türkçe karakter duyarsız arama.
 */
export function turkishIncludes(haystack: string, needle: string): boolean {
  if (!haystack || !needle) return false;
  return toTurkishLower(haystack).includes(toTurkishLower(needle));
}